import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { cx } from "../cx";
import { Spinner } from "../Spinner";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "default" | "text" | "danger" | "link";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** Stretches to the container width (auth pages). */
  block?: boolean;
  /** Legacy `cl-btn` uppercases its label; dialogs' text "Cancel" and auth buttons do not. */
  uppercase?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    iconLeft,
    iconRight,
    block = false,
    uppercase,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  const upper = uppercase ?? (variant !== "text" && variant !== "link");
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        styles.button,
        styles[variant],
        styles[size],
        block && styles.block,
        upper && styles.upper,
        loading && styles.loading,
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={16} className={styles.spinner} /> : iconLeft}
      {children !== undefined && <span className={styles.label}>{children}</span>}
      {iconRight}
    </button>
  );
});
